/**
 * useEmployerProfile Hook
 * Task: 32.1 - Auth Foundation Components
 *
 * React Query hook that fetches employer profile data from employer_user_profiles table.
 * Returns employer-specific authorization data like primary representative status.
 *
 * Relocated to src/hooks/ to allow shared component usage (e.g., role-guard)
 * without cross-feature imports.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/lib/auth';

export interface EmployerProfile {
  isPrimary: boolean;
  companyId: string | null;
  role: string | null;
}

/**
 * Hook to fetch employer profile data
 *
 * @returns React Query result with EmployerProfile data
 * - data: EmployerProfile object or null if user is not an employer
 * - isLoading: True while fetching
 * - error: Database error if query fails (excludes "no rows" error)
 */
export function useEmployerProfile() {
  const { data: auth } = useUser();
  const userId = auth?.user?.id;

  return useQuery({
    queryKey: ['employer-profile', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employer_user_profiles')
        .select('is_primary_representative, managed_employer_company_id, employer_company_role')
        .eq('user_id', userId!)
        .maybeSingle();

      if (error) throw error;

      // Return null if user has no employer profile
      if (!data) return null;

      return {
        isPrimary: data.is_primary_representative ?? false,
        companyId: data.managed_employer_company_id,
        role: data.employer_company_role,
      };
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
